import React from 'react';
import styled from 'styled-components';
import {
  IconMenu,
  IconSearch,
  PopoverTrigger,
  IconChevronDown,
  IconChevronUp,
  PopoverNavigation,
  PopoverNavigationLink,
  Backdrop,
} from '@spotify-internal/encore-web';
import { useTracking } from '../../Tracking';
import { RequireFeatureFlag } from '../../Auth/RequireFeatureFlag';
import { useCurrentUser } from '../../../@mrkt/useCurrentUser';
import { useLayoutContext } from '../LayoutState';
import { UserState } from './types';
import { Link, ResponsiveLogo, Modal, ButtonTertiaryV5AsLink } from '../../ui';
import { useScrollYPosition } from '../../utils';
import { NavSideMenu } from './NavSideMenu';
import { OpenSideMenuButton } from './NavSideMenu.styles';
import {
  NavBarWrapper,
  LogoArea,
  LinksArea,
  ActionsArea,
  MenuButtonArea,
  LoginButton,
  ClaimButton,
  MenuLink,
  MenuCategory,
  StyledListItemWithMargin,
  StyledListItemNoMargin,
  StyledPopoverNavigationItem,
} from './NavBar.styles';
import { USE_SEARCH } from './constants';
import { isHelpLive } from '../../Help/util';
import { useRouter } from 'next/router';
import { useT } from '@spotify-internal/i18n-core';
import { NavBarLanguageItem } from './Language';
import { getAccountsDomain } from '../../../../features/src/auth';
import { NavArrowIcon } from './utils';
import { Wait } from '../Wait';

type MenuState = {
  features?: boolean;
  resources?: boolean;
  help?: boolean;
};

type NavBarProps = {
  forceState?: UserState;
  defaultMenuState?: MenuState;
};

const SIDE_MENU_TRIGGER_BREAKPOINT = 992;
const TRACK_CAT = 'Top Nav';
const MENU_CLOSE_DELAY = 500;

const StyledPopoverNavigation = styled(PopoverNavigation)`
  width: 173px;
  [dir='rtl'] & * {
    direction: rtl;
    & > svg {
      transform: rotateY(180deg);
    }
  }
`;

const StyledIconChevronUp = styled(IconChevronUp)`
  transform: translateY(6px);
`;

const StyledIconChevronDown = styled(IconChevronDown)`
  transform: translateY(4px);
`;

const NavButtonTertiary = styled(ButtonTertiaryV5AsLink)`
  :hover {
    transform: none;
  }
`;

const StyledNavButton = styled(NavButtonTertiary)`
  transform: translateY(-1px);
  :hover {
    transform: translateY(-1px);
  }
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const MenuCategoryLink = React.forwardRef<any, any>(
  ({ children, ...props }, _) => (
    <PopoverNavigationLink {...props}>{children}</PopoverNavigationLink>
  ),
);

const menuTimeouts: { [key: string]: NodeJS.Timeout } = {};

export const NavBar: React.FC<NavBarProps> = ({
  forceState = null,
  defaultMenuState = {},
}) => {
  const t = useT();
  const { sendEvent } = useTracking();
  const layoutContext = useLayoutContext();
  const { navBarBackground, navBarTextColor, navBarBleed } = layoutContext;
  const user = useCurrentUser();
  const [userState, setUserState] = React.useState<UserState>('loading');
  const [menuState, setMenuState] = React.useState<MenuState>(defaultMenuState);
  const [sideMenuOpen, setSideMenuOpened] = React.useState(false);
  const scrollPos = useScrollYPosition();
  const mounted = React.useRef(true);
  const router = useRouter();

  const openSideMenu = React.useCallback(() => {
    document.body.classList.add('sidepanel-active');
    window.addEventListener('keydown', handleKey, true); // eslint-disable-line no-use-before-define
    window.addEventListener('resize', handleResize, false); // eslint-disable-line no-use-before-define
    sendEvent({
      eventCategory: `Frodor - ${TRACK_CAT}`,
      eventAction: 'click',
      eventLabel: 'Open Menu - side',
    });
    setSideMenuOpened(true);
  }, []);

  React.useEffect(() => {
    // focus side menu when opened
    if (sideMenuOpen) {
      const el = document.getElementById('nav-sidemenu');
      el?.focus();
    }
  }, [sideMenuOpen]);

  const closeSideMenu = React.useCallback(() => {
    document.body.classList.remove('sidepanel-active');
    window.removeEventListener('keydown', handleKey, false); // eslint-disable-line no-use-before-define
    window.removeEventListener('resize', handleResize, false); // eslint-disable-line no-use-before-define
    sendEvent({
      eventCategory: `Frodor - ${TRACK_CAT}`,
      eventAction: 'click',
      eventLabel: 'Close Menu - side',
    });
    setSideMenuOpened(false);
  }, []);

  const openMenu = (key: keyof MenuState) => {
    setMenuState({ [key]: true }); // closes all other menus
    sendEvent({
      eventCategory: `Frodor - ${TRACK_CAT}`,
      eventAction: 'click',
      eventLabel: `Open Menu - ${key}`,
    });
  };

  const closeMenu = (key: keyof MenuState) => {
    if (!mounted.current) return;

    setMenuState(curState => ({ ...curState, [key]: false }));
    sendEvent({
      eventCategory: `Frodor - ${TRACK_CAT}`,
      eventAction: 'click',
      eventLabel: `Close Menu - ${key}`,
    });
  };

  // event handlers for dropdown menus
  const getMenuHandlers = (key: keyof MenuState) => {
    return {
      onMouseEnter: () => {
        // open menu and clear timeouts
        const to = menuTimeouts[key];
        if (to) {
          clearTimeout(to);
          delete menuTimeouts[key];
        }
        openMenu(key);
      },
      onMouseLeave: () => {
        // close menu on delay for usability
        menuTimeouts[key] = setTimeout(() => closeMenu(key), MENU_CLOSE_DELAY);
      },
      onKeyDown: (e: React.KeyboardEvent) => {
        if (e.key === ' ') {
          e.preventDefault();
          if (menuState[key]) {
            closeMenu(key);
          } else {
            openMenu(key);
          }
        }
      },
      onClick: () => {
        if (!menuState[key]) {
          openMenu(key);
        }
      },
    };
  };

  React.useEffect(() => {
    const handleChangeRouter = () => {
      closeSideMenu();
    };
    router.events.on('routeChangeStart', handleChangeRouter);
    return () => {
      router.events.off('routeChangeStart', handleChangeRouter);
    };
  }, [router.events, closeSideMenu]);

  React.useEffect(() => {
    return () => {
      document.body.classList.remove('sidepanel-active');
      mounted.current = false;
    };
  }, []);

  // set state of nav bar based on current user, or use 'forceState' if specified
  React.useEffect(() => {
    if (forceState) {
      setUserState(forceState);
      return;
    }
    setUserState(
      user
        ? (user.hasAccess && 'claimed') || 'unclaimed'
        : (user === undefined && 'loading') || 'loggedOut',
    );
  }, [user]);

  const handleKey = React.useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      closeSideMenu();
    }
  }, []);

  const handleResize = React.useCallback(() => {
    if (window.innerWidth >= SIDE_MENU_TRIGGER_BREAKPOINT) {
      closeSideMenu();
    }
  }, []);

  const showLogin = userState === 'loggedOut';
  const showLogout = userState === 'claimed' || userState === 'unclaimed';
  const showDashboard = userState === 'claimed';
  const showClaim = userState === 'unclaimed' || userState === 'loggedOut';

  return userState !== 'loading' ? (
    <NavBarWrapper
      customBackground={navBarBackground}
      customTextColor={navBarTextColor}
      bleed={navBarBleed}
      isScrolling={scrollPos !== 0}
      data-testid={`navbar-state-${userState}`}
    >
      <LogoArea>
        <Link
          track={[TRACK_CAT, 'Logo']}
          href="/home/?ref=logo"
          highlight
          data-testid="navigation-logo"
        >
          <ResponsiveLogo />
        </Link>
      </LogoArea>

      <LinksArea>
        <ul id="menu-links">
          <li>
            <MenuLink>
              <NavButtonTertiary
                track={[TRACK_CAT, 'GetStarted']}
                href="/get-started"
                data-testid="navigation-get-started"
                prefetch={false}
                condensedAll
              >
                {t('FRODOR_NAVBAR_SIDE_GET_STARTED', 'Get started', '')}
              </NavButtonTertiary>
            </MenuLink>
          </li>
          {/* Features menu */}
          <li>
            <MenuCategory>
              <div
                data-testid="navigation-menu-features"
                {...getMenuHandlers('features')}
              >
                <PopoverTrigger
                  onShow={() => {}}
                  onHide={() => {}}
                  placement={PopoverTrigger.bottom}
                  overlay={
                    menuState.features && (
                      <StyledPopoverNavigation arrow={PopoverNavigation.top}>
                        <StyledPopoverNavigationItem>
                          <Link
                            data-testid="navigation-features-overview"
                            track={[TRACK_CAT, 'features-overview']}
                            href="/features"
                            component={MenuCategoryLink}
                          >
                            {t(
                              'FRODOR_NAVBAR_FEATURES_OVERVIEW',
                              'Overview',
                              '',
                            )}
                          </Link>
                        </StyledPopoverNavigationItem>
                        <StyledPopoverNavigationItem features>
                          <Link
                            external
                            data-testid="navigation-canvas"
                            track={[TRACK_CAT, 'canvas']}
                            href="/canvas"
                            component={MenuCategoryLink}
                          >
                            Canvas <NavArrowIcon />
                          </Link>
                        </StyledPopoverNavigationItem>
                        <StyledPopoverNavigationItem features>
                          <Link
                            external
                            data-testid="navigation-clips"
                            track={[TRACK_CAT, 'clips']}
                            href="/clips"
                            component={MenuCategoryLink}
                          >
                            Clips <NavArrowIcon />
                          </Link>
                        </StyledPopoverNavigationItem>
                        <StyledPopoverNavigationItem features>
                          <Link
                            data-testid="navigation-discovery-mode"
                            track={[TRACK_CAT, 'discovery-mode']}
                            href="/discovery-mode"
                            component={MenuCategoryLink}
                          >
                            Discovery Mode
                          </Link>
                        </StyledPopoverNavigationItem>
                        <StyledPopoverNavigationItem features>
                          <Link
                            data-testid="navigation-marquee"
                            track={[TRACK_CAT, 'marquee']}
                            href="/marquee"
                            component={MenuCategoryLink}
                          >
                            Marquee
                          </Link>
                        </StyledPopoverNavigationItem>
                        <StyledPopoverNavigationItem features>
                          <Link
                            data-testid="navigation-merch"
                            track={[TRACK_CAT, 'merch']}
                            href="/merch"
                            component={MenuCategoryLink}
                          >
                            {t('FRODOR_NAVBAR_MERCH_LINK', 'Merch', '')}
                          </Link>
                        </StyledPopoverNavigationItem>
                        <StyledPopoverNavigationItem features>
                          <Link
                            data-testid="navigation-playlisting"
                            track={[TRACK_CAT, 'playlisting']}
                            href="/playlisting"
                            component={MenuCategoryLink}
                          >
                            {t(
                              'FRODOR_NAVBAR_PLAYLISTING_LINK',
                              'Playlisting',
                              '',
                            )}
                          </Link>
                        </StyledPopoverNavigationItem>
                        <StyledPopoverNavigationItem features>
                          <Link
                            external
                            data-testid="navigation-promocards"
                            track={[TRACK_CAT, 'promocards']}
                            href="/promocards"
                            component={MenuCategoryLink}
                          >
                            {t(
                              'FRODOR_NAVBAR_PROMO_CARDS',
                              'Promo Cards',
                              'Promo is short for promotional',
                            )}{' '}
                            <NavArrowIcon />
                          </Link>
                        </StyledPopoverNavigationItem>
                      </StyledPopoverNavigation>
                    )
                  }
                >
                  <StyledNavButton href="" track={['', '']} condensedAll>
                    <span>{t('FRODOR_NAVBAR_FEATURES', 'Features', '')}</span>
                    {menuState.features ? (
                      <StyledIconChevronUp
                        iconSize={16}
                        aria-label={t(
                          'FRODOR_NAVBAR_CLOSE_FEATURES',
                          'Close features menu',
                          '',
                        )}
                      />
                    ) : (
                      <StyledIconChevronDown
                        iconSize={16}
                        aria-label={t(
                          'FRODOR_NAVBAR_OPEN_FEATURES',
                          'Open features menu',
                          '',
                        )}
                      />
                    )}
                  </StyledNavButton>
                </PopoverTrigger>
              </div>
            </MenuCategory>
          </li>

          {/* Resources menu */}
          <li>
            <MenuCategory>
              <div
                data-testid="navigation-menu-resources"
                {...getMenuHandlers('resources')}
              >
                <PopoverTrigger
                  onShow={() => {}}
                  onHide={() => {}}
                  placement={PopoverTrigger.bottom}
                  overlay={
                    menuState.resources && (
                      <StyledPopoverNavigation arrow={PopoverNavigation.top}>
                        <StyledPopoverNavigationItem>
                          <Link
                            data-testid="navigation-videos"
                            track={[TRACK_CAT, 'Videos']}
                            href="/videos"
                            component={MenuCategoryLink}
                          >
                            {t('FRODOR_NAVBAR_VIDEOS', 'Videos', '')}
                          </Link>
                        </StyledPopoverNavigationItem>
                        <StyledPopoverNavigationItem>
                          <Link
                            data-testid="navigation-blog"
                            track={[TRACK_CAT, 'Blog']}
                            href="/blog"
                            component={MenuCategoryLink}
                          >
                            {t('FRODOR_NAVBAR_NEWS', 'News', '')}
                          </Link>
                        </StyledPopoverNavigationItem>
                        <StyledPopoverNavigationItem>
                          <Link
                            data-testid="navigation-events"
                            track={[TRACK_CAT, 'Events']}
                            href="/events"
                            component={MenuCategoryLink}
                          >
                            {t('FRODOR_NAVBAR_EVENTS', 'Events', '')}
                          </Link>
                        </StyledPopoverNavigationItem>
                        <StyledPopoverNavigationItem>
                          <Link
                            external
                            data-testid="navigation-infocus"
                            track={[TRACK_CAT, 'In Focus']}
                            href="/infocus?utm_campaign=top-nav&utm_medium=owned-product&utm_source=s4a"
                            component={MenuCategoryLink}
                          >
                            In Focus <NavArrowIcon />
                          </Link>
                        </StyledPopoverNavigationItem>
                        <StyledPopoverNavigationItem>
                          <Link
                            external
                            data-testid="navigation-fanstudy"
                            track={[TRACK_CAT, 'Fan Study']}
                            href="/fanstudy?utm_campaign=top-nav&utm_medium=owned-product&utm_source=s4a"
                            component={MenuCategoryLink}
                          >
                            Fan Study <NavArrowIcon />
                          </Link>
                        </StyledPopoverNavigationItem>
                        <StyledPopoverNavigationItem>
                          <Link
                            external
                            data-testid="navigation-madetobefound"
                            track={[TRACK_CAT, 'Made to Be Found']}
                            href="/found?utm_campaign=top-nav&utm_medium=owned-product&utm_source=s4a"
                            component={MenuCategoryLink}
                          >
                            Made to Be Found <NavArrowIcon />
                          </Link>
                        </StyledPopoverNavigationItem>
                        <StyledPopoverNavigationItem>
                          <Link
                            external
                            data-testid="navigation-loudclear"
                            track={[TRACK_CAT, 'Loud & Clear']}
                            href="/loudandclear?utm_campaign=top-nav&utm_medium=owned-product&utm_source=s4a"
                            component={MenuCategoryLink}
                          >
                            Loud & Clear <NavArrowIcon />
                          </Link>
                        </StyledPopoverNavigationItem>
                        <StyledPopoverNavigationItem>
                          <Link
                            external
                            data-testid="navigation-noteable"
                            track={[TRACK_CAT, 'Noteable']}
                            href="/noteable?utm_campaign=top-nav&utm_medium=owned-product&utm_source=s4a"
                            component={MenuCategoryLink}
                          >
                            Noteable <NavArrowIcon />
                          </Link>
                        </StyledPopoverNavigationItem>
                      </StyledPopoverNavigation>
                    )
                  }
                >
                  <StyledNavButton href="" track={['', '']} condensedAll>
                    <span>{t('FRODOR_NAVBAR_RESOURCES', 'Resources', '')}</span>
                    {menuState.resources ? (
                      <StyledIconChevronUp
                        iconSize={16}
                        aria-label={t(
                          'FRODOR_NAVBAR_RESOURCES_CLOSE',
                          'Close resources menu',
                          '',
                        )}
                      />
                    ) : (
                      <StyledIconChevronDown
                        iconSize={16}
                        aria-label={t(
                          'FRODOR_NAVBAR_RESOURCES_OPEN',
                          'Open resources menu',
                          '',
                        )}
                      />
                    )}
                  </StyledNavButton>
                </PopoverTrigger>
              </div>
            </MenuCategory>
          </li>

          {/* help center link */}
          {isHelpLive && (
            <li>
              <MenuLink>
                <NavButtonTertiary
                  track={[TRACK_CAT, 'Help']}
                  href="/help"
                  data-testid="navigation-help"
                  condensedAll
                >
                  {t('FRODOR_NAVBAR_HELP', 'Help', '')}
                </NavButtonTertiary>
              </MenuLink>
            </li>
          )}

          {USE_SEARCH && (
            <RequireFeatureFlag
              flag="s4a-frodor-test"
              fallback={<></>}
              waiting={<></>}
            >
              <li>
                <MenuLink>
                  <Link
                    track={[TRACK_CAT, 'Search']}
                    href="/search"
                    highlight
                    data-testid="navigation-search"
                    prefetch={false}
                  >
                    <IconSearch />
                  </Link>
                </MenuLink>
              </li>
            </RequireFeatureFlag>
          )}
        </ul>
      </LinksArea>

      <ActionsArea>
        <ul>
          <NavBarLanguageItem />

          {showLogin && (
            <StyledListItemNoMargin color={navBarTextColor}>
              <LoginButton
                data-testid="root-login"
                highlight
                track={[TRACK_CAT, 'Log In']}
                href={`${getAccountsDomain()}/login?continue=${encodeURIComponent(
                  window.location.origin,
                )}`}
                prefetch={false}
              >
                {t('FRODOR_NAVBAR_LOGIN', 'Log in', '')}
              </LoginButton>
            </StyledListItemNoMargin>
          )}
          {showLogout && (
            <StyledListItemNoMargin color={navBarTextColor}>
              <LoginButton
                data-testid="root-logout"
                highlight
                track={[TRACK_CAT, 'Log Out']}
                href={`${getAccountsDomain()}/logout?continue=${encodeURIComponent(
                  window.location.href,
                )}`}
                prefetch={false}
              >
                {t('FRODOR_NAVBAR_LOGOUT', 'Log out', '')}
              </LoginButton>
            </StyledListItemNoMargin>
          )}
          {showClaim && (
            <StyledListItemWithMargin color={navBarTextColor}>
              <ClaimButton
                data-testid="navigation-claim"
                highlight
                track={[TRACK_CAT, `Get Access (${userState})`]}
                href="/claim"
                prefetch={false}
              >
                {t('FRODOR_NAVBAR_GET_ACCESS', 'Get access', '')}
              </ClaimButton>
            </StyledListItemWithMargin>
          )}
          {showDashboard && (
            <StyledListItemWithMargin color={navBarTextColor}>
              <ClaimButton
                track={[TRACK_CAT, 'Dashboard']}
                href="/c"
                highlight
                data-testid="navigation-dashboard"
                prefetch={false}
              >
                {t('FRODOR_NAVBAR_DASHBOARD', 'Dashboard', '')}
              </ClaimButton>
            </StyledListItemWithMargin>
          )}
        </ul>
      </ActionsArea>

      <MenuButtonArea customTextColor={navBarTextColor}>
        <OpenSideMenuButton
          css="padding-right: 0px;"
          buttonSize="md"
          iconOnly={IconMenu}
          tabIndex={0}
          onKeyDown={(e: React.KeyboardEvent) => {
            if (e.key === ' ' || e.key === 'Enter') {
              e.preventDefault();
              if (sideMenuOpen) {
                closeSideMenu();
              } else {
                openSideMenu();
              }
            }
          }}
          onClick={() => openSideMenu()}
          data-testid="navigation-sidemenu-trigger"
          aria-label={t(
            'FRODOR_NAV_RESPONSIVE_OPEN',
            'Open navigation menu',
            'open navigation menu in responsive display',
          )}
        />
      </MenuButtonArea>

      {/* Nav side menu */}
      {sideMenuOpen && (
        <Modal>
          <NavSideMenu userState={userState} onClose={() => closeSideMenu()} />
          <Backdrop onClick={() => closeSideMenu()} />
        </Modal>
      )}
    </NavBarWrapper>
  ) : (
    <Wait />
  );
};
